import braintree from 'braintree-web';
import cn from 'classnames';
import { useEffect, useState } from 'react';

import ErrorMessage from 'components/common/ErrorMessage';
import { BILLING_AGREEMENT } from 'lib/constants/payments';

import PaypalButtonSkeleton from './PaypalButtonSkeleton';

const PaypalButton = ({
  buttonClass = '',
  containerClass = '',
  onErrorMessage = null,
  onHideChangePayment = () => {},
  onPaypalClick = () => {},
  styles = {
    height: 55,
    color: 'blue',
    label: 'paypal',
    size: 'medium',
  },
  onGetPaypalToken,
  onUpdatePayment,
}) => {
  const [loading, setLoading] = useState(false);
  const [initCount, setInitCount] = useState(1);
  const [error, setError] = useState(false);

  const onError = onErrorMessage || (() => setError(true));

  const createClient = async () => {
    try {
      const token = await onGetPaypalToken();
      const braintreeClient = await braintree.client.create({
        authorization: token.clientToken,
      });
      return braintreeClient;
    } catch (e) {
      if (e.code === 'CLIENT_AUTHORIZATION_INVALID' || e.code === 'CLIENT_INVALID_AUTHORIZATION') {
        return false;
      }
    }
  };

  const loadPayPal = async braintreeClient => {
    const payPalCheckout = await braintree.paypalCheckout.create({
      client: braintreeClient,
    });

    try {
      await payPalCheckout.loadPayPalSDK({ intent: 'tokenize', vault: true });
      return payPalCheckout;
    } catch {
      return false;
    }
  };

  const initializeClient = async () => {
    let client = await createClient();

    if (client === false && initCount === 1) {
      setInitCount(initCount + 1);
      client = await createClient();
      if (client) {
        return client;
      } else {
        return false;
      }
    } else {
      return client;
    }
  };

  const renderPayPalButton = async () => {
    setLoading(true);

    const client = await initializeClient();
    let payPalCheckout;

    if (client) {
      payPalCheckout = await loadPayPal(client);
    } else {
      onError();
      setLoading(false);
      return;
    }

    if (!payPalCheckout || !window.paypal) {
      onError();
      setLoading(false);
      return;
    }

    // prettier-ignore
    window.paypal
      .Buttons({
        fundingSource: window.paypal.FUNDING.PAYPAL,
        style: styles,
        createBillingAgreement: function () {
          return payPalCheckout.createPayment({
            flow: 'vault',
            locale: 'en_US',
            billingAgreementDescription: BILLING_AGREEMENT,
            enableShippingAddress: true,
          });
        },
        onApprove: function (data) {
          return payPalCheckout.tokenizePayment(data, async function (err, payload) {
            const updateResponse = await onUpdatePayment({
              vendor: 'braintree',
              token: payload.nonce,
              details: payload.details,
            });
            if (updateResponse.ok) {
              return onHideChangePayment();
            } else {
              setLoading(false);
              onError();
            }
          });
        },
        onClick: onPaypalClick
      })
      .render('#paypalButtonContainer').then(() => setLoading(false));
  };

  useEffect(() => {
    renderPayPalButton();
  }, []);

  return (
    <>
      {error && (
        <ErrorMessage message="An unexpected error occurred with PayPal. Please refresh and try again." />
      )}
      <div className={cn(containerClass, 'relative min-w-40', { hidden: error })}>
        {loading && <PaypalButtonSkeleton />}
        <div id="paypalButtonContainer" data-testid="paypal-container" className={buttonClass} />
      </div>
    </>
  );
};

PaypalButton.propTypes = {
  onGetPaypalToken: PropTypes.func.isRequired,
  onUpdatePayment: PropTypes.func.isRequired,
  onPaypalClick: PropTypes.func,
  onHideChangePayment: PropTypes.func,
  onErrorMessage: PropTypes.func,
  styles: PropTypes.object,
  containerClass: PropTypes.string,
  buttonClass: PropTypes.string,
};

export default PaypalButton;
